import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { MapPin, Sprout, BookOpen, Landmark, Tv, Goal, Quote } from 'lucide-react'
import profile from '../data/profile.json'

const PROFILE_PHOTO = '/abder.png'

const iconMap = {
  Sprout,
  BookOpen,
  Landmark,
  Tv,
  Goal,
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
}

export default function About() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section id="about" className="relative py-20 overflow-hidden bg-white lg:py-32">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary-50/50 to-transparent" />
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-rose-valley-100/30 blur-3xl" />

      <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center"
        >
          {/* Left Column - Story */}
          <div className="space-y-8">
            {/* Photo for mobile - hidden on desktop */}
            <motion.div 
              variants={itemVariants}
              className="flex justify-center lg:hidden"
            >
              <div className="relative">
                <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-primary-600 to-rose-valley-500 blur opacity-30" />
                <div className="relative w-48 h-48 overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src={PROFILE_PHOTO}
                    alt={profile.identity.name}
                    className="object-cover object-center w-full h-full"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'flex'
                    }}
                  />
                  <div 
                    className="items-center justify-center hidden w-full h-full text-5xl font-bold text-white bg-gradient-to-br from-primary-600 to-rose-valley-500 font-display"
                  >
                    {profile.identity.name.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-rose-valley-50 text-rose-valley-700">
                <MapPin className="w-4 h-4 mr-1.5" />
                {profile.identity.origin.place}, {profile.identity.origin.region}
              </span>
            </motion.div>

            <motion.h2
              variants={itemVariants}
              className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl"
            >
              De la vallée des roses
              <span className="block mt-1 gradient-text">vers le cloud et l'IA</span>
            </motion.h2>

            <motion.div variants={itemVariants} className="space-y-4 text-gray-600">
              <p className="text-lg leading-relaxed">
                {profile.identity.bio}
              </p>
              <p className="leading-relaxed">
                {profile.identity.story}
              </p>
            </motion.div>

            {/* Quote */}
            <motion.blockquote
              variants={itemVariants}
              className="relative p-6 border-l-4 rounded-r-2xl bg-gradient-to-r from-primary-50 to-transparent border-primary-500"
            >
              <Quote className="absolute w-8 h-8 -top-2 -left-2 text-primary-200" />
              <p className="text-lg italic text-gray-700">
                "{profile.identity.mission}"
              </p>
            </motion.blockquote>
          </div>

          {/* Right Column - Photo + Passions Grid */}
          <motion.div variants={itemVariants} className="space-y-6">
            {/* Large Photo for Desktop */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-primary-600 via-rose-valley-500 to-primary-600 blur-lg opacity-20 animate-gradient" />
                <div className="relative overflow-hidden rounded-3xl shadow-2xl aspect-[4/3]">
                  <img
                    src={PROFILE_PHOTO}
                    alt={profile.identity.name}
                    className="object-cover object-center w-full h-full"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'flex'
                    }}
                  />
                  <div 
                    className="items-center justify-center hidden w-full h-full text-7xl font-bold text-white bg-gradient-to-br from-primary-600 to-rose-valley-500 font-display"
                  >
                    {profile.identity.name.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900">
              Ce qui me passionne au quotidien
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {profile.passions.map((passion, index) => {
                const Icon = iconMap[passion.icon] || Sprout
                return (
                  <motion.div
                    key={passion.name}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02, y: -4 }}
                    className="p-5 transition-shadow bg-white border border-gray-100 shadow-sm group rounded-2xl hover:shadow-lg hover:border-primary-100"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex items-center justify-center w-12 h-12 transition-colors rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-primary-50 group-hover:to-rose-valley-50">
                        <Icon className="w-6 h-6 text-gray-600 transition-colors group-hover:text-primary-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{passion.name}</h4>
                        <p className="mt-1 text-sm text-gray-500">{passion.description}</p>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>

            {/* Stats */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-3 gap-4 p-6 mt-8 rounded-2xl bg-gradient-to-br from-primary-600 to-rose-valley-600"
            >
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{profile.identity.experience.years}+</p>
                <p className="mt-1 text-sm text-white/70">Années d'expérience</p>
              </div>
              <div className="text-center border-x border-white/20">
                <p className="text-3xl font-bold text-white">3</p>
                <p className="mt-1 text-sm text-white/70">Entreprises fondées</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white">10+</p>
                <p className="mt-1 text-sm text-white/70">Épisodes podcast</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
